/**
 * One AI employee, in depth: its queue, the questions it asked, the digests
 * it wrote and its credentials.
 *
 * The interventions here are the ones an owner needs while looking at the
 * evidence — cancel or retry a job, answer or deny a question, rotate a
 * token, suspend the employee — each hitting the same endpoint the MCP/CLI
 * surfaces use.
 *
 * The drawer is URL-backed: `?agent=<uuid>&tab=<tab>&job=<uuid>` on
 * /team. A question, a job, an employee are all linkable — the
 * drill-down chain (question ⇄ job ⇄ delegation lineage ⇄ employee) is
 * navigation, not prose. Browser back closes what it opened.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { clsx } from "clsx";

import {
  adminApi,
  type AiTeamAgentDetail,
  type AiTeamJob,
  type JobActionResult,
  type JobStatus,
} from "../../api/admin";
import { Markdown } from "../../components/Markdown";
import { Pill } from "../../components/Pill";
import { ShortId } from "../../components/ShortId";
import { QueryBoundary } from "../../components/QueryBoundary";
import { Skeleton } from "../../components/Skeleton";
import { Drawer } from "../../components/admin/Drawer";
import { JsonExpand } from "../../components/admin/JsonExpand";
import { SecretOnce } from "../../components/admin/SecretOnce";
import { useConfirm } from "../../components/confirm/context";
import { useToast } from "../../components/toast/context";
import {
  JOB_STATUSES,
  type AgentDrawerTab,
  type QuestionFilter,
  agentDrawerUrl,
  agentStateTone,
  isTerminalJobStatus,
  jobStatusTone,
} from "../../lib/aiTeam";
import { formatRelative } from "../../lib/formatRelative";
import { QuestionCard, QuestionStatusChips } from "./QuestionCard";

import { EmployeeGoals } from "./EmployeeGoals";

type Tab = AgentDrawerTab;

const TABS: { value: Tab; label: string }[] = [
  { value: "goals", label: "Goals" },
  { value: "jobs", label: "Jobs" },
  { value: "questions", label: "Questions" },
  { value: "digests", label: "Digests" },
  { value: "settings", label: "Settings" },
];

interface Props {
  agentId: string | null;
  onClose: () => void;
}

export function EmployeeDrawer({ agentId, onClose }: Props) {
  return (
    <Drawer
      open={!!agentId}
      onClose={onClose}
      eyebrow="Team"
      title="AI employee"
      widthClass="w-[min(1180px,94vw)] max-w-full"
    >
      {agentId ? <DrawerBody agentId={agentId} /> : null}
    </Drawer>
  );
}

function DrawerBody({ agentId }: { agentId: string }) {
  const agentQuery = adminApi.aiTeam.agent(agentId);
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const tab: Tab = TABS.some((t) => t.value === tabParam)
    ? (tabParam as Tab)
    : "goals";
  // Tab flips replace the history entry — back should close the drawer (or
  // leave a job detail), not replay every tab the operator glanced at.
  const setTab = (next: Tab) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", next);
        p.delete("job");
        return p;
      },
      { replace: true },
    );
  };

  return (
    <QueryBoundary
      query={agentQuery}
      loading={
        <div className="space-y-3">
          <Skeleton height={28} width="50%" />
          <Skeleton height={20} width="35%" />
          <Skeleton height={200} width="100%" />
        </div>
      }
    >
      {(agent) => (
        <div className="space-y-5">
          <Identity agent={agent} />

          <nav
            className="flex items-center gap-2 pb-3 border-b border-rule"
            aria-label="Employee sections"
          >
            {TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                className={clsx(
                  "btn btn-sm",
                  tab === t.value ? "btn-main" : "btn-ghost",
                )}
                onClick={() => setTab(t.value)}
              >
                {t.label}
              </button>
            ))}
          </nav>

          {tab === "goals" ? <EmployeeGoals agentId={agentId} /> : null}
          {tab === "jobs" ? <JobsTab agentId={agentId} /> : null}
          {tab === "questions" ? <QuestionsTab agent={agent} /> : null}
          {tab === "digests" ? <DigestsTab agentId={agentId} /> : null}
          {tab === "settings" ? <SettingsTab agent={agent} /> : null}
        </div>
      )}
    </QueryBoundary>
  );
}

function Identity({ agent }: { agent: AiTeamAgentDetail }) {
  return (
    <div className="space-y-3">
      {agent.disabled_at ? (
        <div className="callout callout--danger" role="status">
          Suspended {formatRelative(agent.disabled_at)}. Its token is refused on
          the next call and its cron rows were disabled — resume does not
          re-enable them.
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-heading font-semibold text-ink text-lg truncate">
            {agent.name || agent.agent_slug || "(unnamed)"}
          </div>
          <div className="font-mono text-xs text-ink-low truncate">
            {agent.agent_slug} · {agent.email}
          </div>
        </div>
        <Pill value={agent.state} tone={agentStateTone(agent.state)} />
      </div>

      <div className="flex flex-wrap items-center gap-4 text-sm text-ink-medium">
        <span>
          <span className="tnum font-medium text-ink">
            {agent.submitted_count}
          </span>{" "}
          submitted
        </span>
        <span>
          <span className="tnum font-medium text-ink">{agent.working_count}</span>{" "}
          working
        </span>
        <span>
          <span className="tnum font-medium text-ink">{agent.parked_count}</span>{" "}
          parked
        </span>
        <span>
          <span className="tnum font-medium text-ink">{agent.open_questions}</span>{" "}
          open questions
        </span>
        <span className="text-ink-low text-xs ml-auto">
          seen {formatRelative(agent.last_seen_at)} ·{" "}
          {agent.stream_connected !== undefined
            ? `receiver ${agent.stream_connected ? "connected" : "not connected"} · `
            : ""}
          digest {formatRelative(agent.last_digest_at)}
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Jobs
// ---------------------------------------------------------------------------

/**
 * Retry says two things worth reading: whether the clicked row went back on
 * the queue or folded into one already carrying the work, and how many
 * duplicate failures were cleaned up with it.
 */
function retryToast(r: JobActionResult): string {
  const head = r.action === "already_queued"
    ? `Already queued (${r.job.status})`
    : "Requeued";
  const n = r.collapsed?.length ?? 0;
  return n ? `${head} · ${n} duplicate failure${n === 1 ? "" : "s"} cleaned up` : head;
}

function JobsTab({ agentId }: { agentId: string }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<JobStatus | "">("");
  const jobsQuery = adminApi.aiTeam.agentJobs(agentId, status || undefined);
  const cancelMutation = adminApi.aiTeam.cancelJob();
  const retryMutation = adminApi.aiTeam.retryJob();
  const toast = useToast();
  const confirm = useConfirm();

  const cancelJob = async (id: string) => {
    const ok = await confirm({
      title: "Cancel this job?",
      body: "A queued job is dropped. A running job is flagged and ends as canceled when its session completes it.",
      confirmLabel: "Cancel job",
      cancelLabel: "Keep it",
      tone: "danger",
    });
    if (!ok) return;
    cancelMutation.mutate(id, {
      onSuccess: (r) => toast.ok(`Job ${r.action ?? "canceled"}`),
      onError: (e) => toast.error(e),
    });
  };

  const jobId = searchParams.get("job");
  // Opening a job pushes a history entry — browser back returns to the queue.
  const openJob = (id: string) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("job", id);
      return p;
    });
  };
  const closeJob = () => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.delete("job");
      return p;
    });
  };

  if (jobId) {
    return (
      <JobDetailPanel
        jobId={jobId}
        onBack={closeJob}
        onCancel={() => void cancelJob(jobId)}
        onRetry={() =>
          retryMutation.mutate(jobId, {
            onSuccess: (r) => toast.ok(retryToast(r)),
            onError: (e) => toast.error(e),
          })
        }
      />
    );
  }

  return (
    <>
      <div className="flex items-end gap-3 mb-4">
        <div>
          <label className="label" htmlFor="job-status">
            Status
          </label>
          <select
            id="job-status"
            className="input"
            style={{ minWidth: 180 }}
            value={status}
            onChange={(e) => setStatus(e.target.value as JobStatus | "")}
          >
            <option value="">all</option>
            {JOB_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        <div className="ml-auto text-sm text-ink-low pb-2">
          <span className="tnum font-medium text-ink">
            {jobsQuery.data?.length ?? 0}
          </span>{" "}
          jobs
        </div>
      </div>

      <QueryBoundary
        query={jobsQuery}
        empty={
          <div className="empty-state">
            <div className="title">No jobs</div>
            <p className="hint">
              Nothing queued for this employee in the selected status.
            </p>
          </div>
        }
        isEmpty={(rows) => rows.length === 0}
      >
        {(rows) => (
          <div className="panel panel--flush overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th>Status</th>
                  <th>Attempts</th>
                  <th>Queued</th>
                  <th>Ended</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((job) => (
                  <JobRow
                    key={job.id}
                    job={job}
                    onOpen={() => openJob(job.id)}
                    onCancel={() => void cancelJob(job.id)}
                    onRetry={() =>
                      retryMutation.mutate(job.id, {
                        onSuccess: (r) => toast.ok(retryToast(r)),
                        onError: (e) => toast.error(e),
                      })
                    }
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

function JobRow({
  job,
  onOpen,
  onCancel,
  onRetry,
}: {
  job: AiTeamJob;
  onOpen: () => void;
  onCancel: () => void;
  onRetry: () => void;
}) {
  const terminal = isTerminalJobStatus(job.status);
  const name = (job.args?.name as string | undefined) || job.job_type;

  return (
    <tr>
      <td>
        <button
          type="button"
          className="font-mono text-sm text-ink link-quiet"
          style={{ cursor: "pointer" }}
          onClick={onOpen}
        >
          {name}
        </button>
        <div className="text-xs text-ink-low">
          {job.job_type} · <ShortId id={job.id} />
          {job.waiting_on_id ? (
            <span style={{ color: "var(--c-yellow-dark)" }}>
              {" "}
              · waiting on a question
            </span>
          ) : null}
          {job.parent_job_id ? " · delegated" : null}
        </div>
      </td>
      <td>
        <Pill value={job.status} tone={jobStatusTone(job.status)} />
        {job.cancel_requested && !terminal ? (
          <div className="text-xs text-ink-low mt-1">cancel requested</div>
        ) : null}
      </td>
      <td className="text-sm tnum text-ink-medium">
        {job.attempt_count}/{job.max_attempts}
      </td>
      <td className="text-sm text-ink-low tnum">
        {formatRelative(job.scheduled_at)}
      </td>
      <td className="text-sm text-ink-low tnum">
        {job.ended_at ? formatRelative(job.ended_at) : "—"}
      </td>
      <td>
        {terminal ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
            Retry
          </button>
        ) : (
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
            Cancel
          </button>
        )}
      </td>
    </tr>
  );
}

/**
 * One job with everything it is connected to: timeline, args and result,
 * the job that delegated it, the work it delegated onward, and the questions
 * it is (or was) parked on — answerable in place, because the question is
 * exactly why a parked job is parked.
 */
function JobDetailPanel({
  jobId,
  onBack,
  onCancel,
  onRetry,
}: {
  jobId: string;
  onBack: () => void;
  onCancel: () => void;
  onRetry: () => void;
}) {
  const detailQuery = adminApi.aiTeam.jobDetail(jobId);
  const answerMutation = adminApi.aiTeam.answerQuestion();
  const denyMutation = adminApi.aiTeam.denyQuestion();
  const toast = useToast();

  return (
    <>
      <button type="button" className="btn btn-ghost btn-sm mb-3" onClick={onBack}>
        ← back to queue
      </button>
      <QueryBoundary
        query={detailQuery}
        loading={
          <div className="space-y-3">
            <Skeleton height={24} width="40%" />
            <Skeleton height={160} width="100%" />
          </div>
        }
      >
        {({ job, parent, children, questions }) => {
          const terminal = isTerminalJobStatus(job.status);
          const name = (job.args?.name as string | undefined) || job.job_type;
          return (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-sm text-ink">{name}</div>
                  <div className="text-xs text-ink-low">
                    {job.job_type} · <ShortId id={job.id} /> · {job.scope_type}:{job.scope_id.slice(0, 8)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Pill value={job.status} tone={jobStatusTone(job.status)} />
                  {terminal ? (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={onRetry}
                    >
                      Retry
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={onCancel}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              <div className="text-sm text-ink-medium flex flex-wrap gap-x-4 gap-y-1">
                <span>
                  queued{" "}
                  <span className="tnum">{formatRelative(job.scheduled_at)}</span>
                </span>
                <span>
                  started{" "}
                  <span className="tnum">
                    {job.started_at ? formatRelative(job.started_at) : "—"}
                  </span>
                </span>
                <span>
                  ended{" "}
                  <span className="tnum">
                    {job.ended_at ? formatRelative(job.ended_at) : "—"}
                  </span>
                </span>
                <span>
                  attempts{" "}
                  <span className="tnum">
                    {job.attempt_count}/{job.max_attempts}
                  </span>
                </span>
              </div>

              {job.error ? (
                <div className="text-sm text-danger">
                  {job.error}
                </div>
              ) : null}

              <div className="space-y-1">
                {job.args ? <JsonExpand value={job.args} /> : null}
                {job.result ? (
                  <JsonExpand value={job.result} preview="result" />
                ) : null}
              </div>

              {parent ? (
                <div className="text-sm">
                  <span className="text-ink-low">delegated by </span>
                  <JobLink job={parent} />
                </div>
              ) : null}

              {children.length > 0 ? (
                <div className="text-sm space-y-1">
                  <div className="text-xs text-ink-low">
                    Delegated onward
                  </div>
                  {children.map((c) => (
                    <div key={c.id} className="flex items-center gap-2">
                      <JobLink job={c} />
                      <Pill value={c.status} tone={jobStatusTone(c.status)} />
                    </div>
                  ))}
                </div>
              ) : null}

              {questions.length > 0 ? (
                <div className="space-y-3">
                  <div className="text-xs text-ink-low">
                    Questions on this job
                  </div>
                  {questions.map((q) => (
                    <QuestionCard
                      key={q.id}
                      question={q}
                      showAsker
                      onAnswer={async (body) => {
                        try {
                          await answerMutation.mutateAsync({ id: q.id, ...body });
                          toast.ok("Answered");
                        } catch (e) {
                          toast.error(e);
                          throw e; // Keeps the draft — the card clears on success only.
                        }
                      }}
                      onDeny={() =>
                        denyMutation.mutate(
                          { id: q.id },
                          {
                            onSuccess: (r) =>
                              toast.ok(r.status === "denied"
                                  ? "Denied"
                                  : `Already ${r.status} — deny arrived late`),
                            onError: (e) => toast.error(e),
                          },
                        )
                      }
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        }}
      </QueryBoundary>
    </>
  );
}

/**
 * A job reference that navigates to the job's own detail — in its owning
 * employee's drawer when the job lives in an agent scope, so a delegation
 * chain walks across employees the way the work actually flowed.
 */
function JobLink({ job }: { job: AiTeamJob }) {
  const name = (job.args?.name as string | undefined) || job.job_type;
  if (job.scope_type !== "agent") {
    return <span className="font-mono text-sm text-ink">{name}</span>;
  }
  return (
    <Link
      className="font-mono text-sm text-ink link-quiet"
      to={agentDrawerUrl(job.scope_id, { tab: "jobs", jobId: job.id })}
    >
      {name}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

function QuestionsTab({ agent }: { agent: AiTeamAgentDetail }) {
  const [filter, setFilter] = useState<QuestionFilter>("pending");
  // Server-side filter: the endpoint scopes to this asker before applying
  // its limit, so the history here is this employee's — not a client-side
  // slice of a team-wide page that silently truncates on a busy team.
  const questionsQuery = adminApi.aiTeam.questions(filter, {
    requestedBy: agent.id,
  });
  const answerMutation = adminApi.aiTeam.answerQuestion();
  const denyMutation = adminApi.aiTeam.denyQuestion();
  const toast = useToast();

  return (
    <>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <QuestionStatusChips value={filter} onChange={setFilter} />
      </div>
      <QueryBoundary
        query={questionsQuery}
        empty={
          filter === "pending" ? (
            <div className="empty-state">
              <div className="title">No open questions</div>
              <p className="hint">
                This employee is not waiting on anyone. Resolved history is one
                filter away.
              </p>
            </div>
          ) : (
            <div className="empty-state">
              <div className="title">Nothing here</div>
              <p className="hint">
                No “{filter}” questions from this employee in the retained log.
              </p>
            </div>
          )
        }
        isEmpty={(rows) => rows.length === 0}
      >
        {(rows) => (
          <div className="space-y-3">
            {rows.map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                onAnswer={async (body) => {
                  try {
                    await answerMutation.mutateAsync({ id: q.id, ...body });
                    toast.ok("Answered");
                  } catch (e) {
                    toast.error(e);
                    throw e; // Keeps the draft — the card clears on success only.
                  }
                }}
                onDeny={() =>
                  denyMutation.mutate(
                    { id: q.id },
                    {
                      onSuccess: (r) =>
                        toast.ok(r.status === "denied"
                            ? "Denied"
                            : `Already ${r.status} — deny arrived late`),
                      onError: (e) => toast.error(e),
                    },
                  )
                }
              />
            ))}
          </div>
        )}
      </QueryBoundary>
    </>
  );
}

// ---------------------------------------------------------------------------
// Digests
// ---------------------------------------------------------------------------

function DigestsTab({ agentId }: { agentId: string }) {
  const reportsQuery = adminApi.aiTeam.reports(14);

  return (
    <QueryBoundary
      query={reportsQuery}
      empty={
        <div className="empty-state">
          <div className="title">No digests</div>
          <p className="hint">
            Digests appear here once the employee completes a job that returns
            a `digest_md` result.
          </p>
        </div>
      }
      isEmpty={(data) =>
        data.digests.filter((d) => d.agent_id === agentId).length === 0
      }
    >
      {(data) => (
        <div className="space-y-4">
          {data.digests
            .filter((d) => d.agent_id === agentId)
            .map((d) => (
              <section key={d.job_id} className="panel panel--flush">
                <div className="panel-header">
                  <h3>{formatRelative(d.date)}</h3>
                  <span className="font-mono text-xs text-ink-low">
                    {d.date?.slice(0, 10)}
                  </span>
                </div>
                <div className="panel-body">
                  <Markdown text={d.digest_md} />
                </div>
              </section>
            ))}
        </div>
      )}
    </QueryBoundary>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

function SettingsTab({ agent }: { agent: AiTeamAgentDetail }) {
  const suspendMutation = adminApi.aiTeam.suspendAgent();
  const resumeMutation = adminApi.aiTeam.resumeAgent();
  const rotateMutation = adminApi.aiTeam.rotateToken();
  const revokeMutation = adminApi.aiTeam.revokeToken();
  const toast = useToast();
  const confirm = useConfirm();
  const [minted, setMinted] = useState<string | null>(null);

  const suspended = !!agent.disabled_at;

  const toggleSuspension = async () => {
    if (suspended) {
      const ok = await confirm({
        title: `Resume ${agent.agent_slug}?`,
        body: "Its cron rows stay disabled — re-enable the ones you still want under Cron jobs.",
        confirmLabel: "Resume",
      });
      if (!ok) return;
      resumeMutation.mutate(agent.id, {
        onSuccess: () => toast.ok("Resumed"),
        onError: (e) => toast.error(e),
      });
      return;
    }
    const ok = await confirm({
      title: `Suspend ${agent.agent_slug}?`,
      body: "Its token stops working on the next call and its cron rows are disabled.",
      confirmLabel: "Suspend",
      tone: "danger",
    });
    if (!ok) return;
    suspendMutation.mutate(agent.id, {
      onSuccess: () => toast.ok("Suspended"),
      onError: (e) => toast.error(e),
    });
  };

  return (
    <div className="space-y-5">

      <section className="panel panel--flush">
        <div className="panel-header">
          <h3>Roles</h3>
        </div>
        <div className="panel-body flex flex-wrap gap-2">
          {agent.role_names.length === 0 ? (
            <span className="text-sm text-ink-low">No roles granted.</span>
          ) : (
            agent.role_names.map((r) => (
              <span key={r} className="tag-chip">
                {r}
              </span>
            ))
          )}
        </div>
      </section>

      {minted ? <SecretOnce secret={minted} label="New agent token" /> : null}

      <section className="panel panel--flush">
        <div className="panel-header">
          <h3>Tokens</h3>
          <button
            type="button"
            className="btn btn-second btn-sm"
            disabled={rotateMutation.isPending}
            onClick={async () => {
              const ok = await confirm({
                title: "Rotate the token?",
                body: "Every other active token for this employee is revoked immediately.",
                confirmLabel: "Rotate",
                tone: "danger",
              });
              if (!ok) return;
              rotateMutation.mutate(
                { agentId: agent.id },
                {
                  onSuccess: (t) => {
                    setMinted(t.token);
                    toast.ok(`Rotated — ${t.revoked_others} token(s) revoked`);
                  },
                  onError: (e) => toast.error(e),
                },
              );
            }}
          >
            {rotateMutation.isPending ? "Rotating…" : "Rotate"}
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Prefix</th>
                <th>State</th>
                <th>Last used</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {agent.tokens.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-sm text-ink-low">
                    No tokens.
                  </td>
                </tr>
              ) : (
                agent.tokens.map((t) => (
                  <tr key={t.id}>
                    <td className="font-mono text-sm">{t.token_prefix}…</td>
                    <td>
                      <Pill
                        value={t.state}
                        tone={t.state === "active" ? "ok" : "muted"}
                      />
                    </td>
                    <td className="text-sm text-ink-low tnum">
                      {formatRelative(t.last_used_at)}
                    </td>
                    <td className="text-sm text-ink-low tnum">
                      {formatRelative(t.created_at)}
                    </td>
                    <td>
                      {t.state === "active" ? (
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={async () => {
                            const ok = await confirm({
                              title: `Revoke token ${t.token_prefix}…?`,
                              body: "The employee loses access on its next call.",
                              confirmLabel: "Revoke",
                              tone: "danger",
                            });
                            if (!ok) return;
                            revokeMutation.mutate(
                              { agentId: agent.id, tokenId: t.id },
                              {
                                onSuccess: () => toast.ok("Revoked"),
                                onError: (e) => toast.error(e),
                              },
                            );
                          }}
                        >
                          Revoke
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="panel panel--flush">
        <div className="panel-header">
          <h3>Cron</h3>
          <Link
            className="text-sm link"
            to={`/admin/cron-jobs?scope_type=agent&q=${encodeURIComponent(
              agent.agent_slug || agent.name || "",
            )}`}
          >
            Manage in Cron jobs →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Schedule</th>
                <th>Model</th>
                <th>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {agent.cron_jobs.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-sm text-ink-low">
                    Nothing scheduled into this queue.
                  </td>
                </tr>
              ) : (
                agent.cron_jobs.map((c) => (
                  <tr key={c.id}>
                    <td className="text-sm text-ink">{c.name}</td>
                    <td className="font-mono text-xs">{c.schedule || "—"}</td>
                    <td className="text-sm text-ink-medium">{c.model || "—"}</td>
                    <td>
                      <Pill
                        value={c.enabled ? "enabled" : "disabled"}
                        tone={c.enabled ? "ok" : "muted"}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="flex justify-end pt-2 border-t border-rule">
        <button
          type="button"
          className={suspended ? "btn btn-ghost" : "btn btn-ghost btn-danger"}
          disabled={suspendMutation.isPending || resumeMutation.isPending}
          onClick={toggleSuspension}
        >
          {suspended ? "Resume employee" : "Suspend employee"}
        </button>
      </div>
    </div>
  );
}
